import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { callGetWebservice } from '../hooks/api';
import { setCartCount } from '../store/reducers/auth';
import { setMerchantSettings } from '../store/reducers/general';
import { openModal } from '../store/reducers/modal';
import { cartsData, currencyData, languageData, timeStamp, userData } from '../utils/constants';
import { getFromLocalStorage, saveToLocalStorage } from '../utils/localStorageUtils';

const Header = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const merchantSettings = useSelector((state) => state.general.merchantSettings);
    const cartsCount = useSelector((state) => state.auth.cartsCount);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const [currency, setCurrency] = useState('');
    const [language, setLanguage] = useState('');
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCurrencyDropdown(false);
                setShowLanguageDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    useEffect(() => {
        if (!merchantSettings || Object.keys(merchantSettings).length === 0) {
            callGetWebservice('merchant', {}, (response) => {
                dispatch(setMerchantSettings(response.data));
            });
        }

        let cartData = getFromLocalStorage(cartsData);
        dispatch(setCartCount(cartData ? cartData.length : 0));
    }, [dispatch, merchantSettings]);

    // Currency & Language initialization effect
    useEffect(() => {
        if (!merchantSettings || merchantSettings.length === 0) return;

        let storeCurrency = getFromLocalStorage(currencyData);
        let storeLanguage = getFromLocalStorage(languageData);

        const getTimeStamp = getFromLocalStorage(timeStamp);
        if (getTimeStamp != null) {
            const currentTime = Date.now();
            const sixHours = 6 * 60 * 60 * 1000;
            if (currentTime - getTimeStamp > sixHours) {
                saveToLocalStorage(currencyData, null);
                saveToLocalStorage(languageData, null);
                storeCurrency = null;
                storeLanguage = null;
            }
        }

        if (storeCurrency == null || storeLanguage == null) {
            let cur = merchantSettings?.currencies?.find(c => c.is_default);
            let lang = merchantSettings?.languages?.find(c => c.is_default);
            setCurrency(cur?.currency.code || '');
            setLanguage(lang?.language.name || '');
            const timestampNow = Date.now();
            saveToLocalStorage(timeStamp, timestampNow);
            saveToLocalStorage(currencyData, cur);
            saveToLocalStorage(languageData, lang);
        } else {
            setCurrency(storeCurrency?.currency?.code || '');
            setLanguage(storeLanguage?.language?.name || '');
        }
    }, [merchantSettings]);

    const onLoginClick = () => {
        const user = getFromLocalStorage(userData);
        if (user?.token) navigate('/myaccount');
        else navigate('/login');
    };

    const handleCurrencySelect = (curr) => {
        saveToLocalStorage(currencyData, curr);
        setCurrency(curr?.currency?.code);
        window.location.reload();
    };

    const handleLanguageSelect = (lang) => {
        saveToLocalStorage(languageData, lang);
        setLanguage(lang?.language?.name);
        window.location.reload();
    };

    return (
        <>
            <header className={`main-header glass ${isScrolled ? 'scrolled shadow-sm' : ''}`} style={{
                position: 'sticky', top: 0, zIndex: 1000, transition: 'var(--transition)'
            }}>
                <div className="container-fluid px-3 px-xl-5 py-2 py-md-3">
                    <nav className="d-flex align-items-center justify-content-between">
                        {/* Left: Mobile Menu Trigger (Hamburger) */}
                        <div className="d-lg-none d-flex align-items-center">
                            <button className="bg-transparent border-0 p-0 text-dark" onClick={toggleMobileMenu} aria-label="Open mobile navigation menu">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="3" y1="12" x2="21" y2="12"></line>
                                    <line x1="3" y1="6" x2="21" y2="6"></line>
                                    <line x1="3" y1="18" x2="21" y2="18"></line>
                                </svg>
                            </button>
                        </div>

                        {/* Left: Desktop Menu */}

                        <div className="d-none d-lg-flex align-items-center gap-2 gap-xl-3">
                            <Link to="/" className="nav-link font-heading text-uppercase fs-xs ls-1 text-dark">Home</Link>
                            <Link to="/productlist" className="nav-link font-heading text-uppercase fs-xs ls-1 text-dark">Menu</Link>
                            <Link to="/store" className="nav-link font-heading text-uppercase fs-xs ls-1 text-dark">Locations</Link>
                            {merchantSettings?.config?.show_home_order === 1 && <Link to="/store?home_outlet=true" className="nav-link font-heading text-uppercase fs-xs ls-1 text-dark">Home Order</Link>}
                            {merchantSettings?.config?.show_event_booking === 1 && <Link to="/book-event" className="nav-link font-heading text-uppercase fs-xs ls-1 text-dark">Book an Event</Link>}
                        </div>

                        {/* Center: Logo */}
                        <Link to="/" className="brand-logo">
                            <img
                                src={merchantSettings?.image || "/assets/img/logo.png"}
                                alt={merchantSettings?.legal_name || "Stylesprint"}
                                style={{ height: '32px', transition: 'var(--transition)' }}
                                className={isScrolled ? 'logo-shrink' : ''}
                            />
                        </Link>

                        {/* Right Group: Currency, Language, Action Pill & CTA */}
                        <div className="d-flex align-items-center gap-2 gap-md-3 gap-lg-4">

                            {/* Currency & Language Selectors */}
                            <div ref={dropdownRef} className="d-none d-md-flex align-items-center gap-3">
                                {/* Currency Selector */}
                                {currency && (
                                    <div className="currency-selector dropdown js-dropdown" style={{ marginRight: '3px' }}>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowCurrencyDropdown(!showCurrencyDropdown);
                                                setShowLanguageDropdown(false);
                                            }}
                                            className="selector-trigger bg-transparent border-0"
                                            title="Currency"
                                            aria-label="Select currency"
                                            aria-haspopup="true"
                                            aria-expanded={showCurrencyDropdown}
                                        >
                                            <span className="selector-label d-none d-lg-inline">Currency:</span>
                                            <span className="selector-value">{currency}</span>
                                            <i className="fa fa-angle-down selector-arrow" />
                                        </button>
                                        {merchantSettings?.currencies?.length > 1 && (
                                            <ul className={`selector-dropdown-menu ${showCurrencyDropdown ? 'show' : ''}`}>
                                                {merchantSettings?.currencies?.map((curr) => (
                                                    <li key={curr.id}>
                                                        <button
                                                            type="button"
                                                            className="selector-dropdown-item bg-transparent border-0 w-100 text-left"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleCurrencySelect(curr);
                                                                setShowCurrencyDropdown(false);
                                                            }}
                                                        >
                                                            {curr.currency.name} ({curr.currency.symbol})
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                {/* Language Selector */}
                                {language && (
                                    <div className="language-selector-wrapper dropdown js-dropdown">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowLanguageDropdown(!showLanguageDropdown);
                                                setShowCurrencyDropdown(false);
                                            }}
                                            className="selector-trigger bg-transparent border-0"
                                            title="Language"
                                            aria-label="Select language"
                                            aria-haspopup="true"
                                            aria-expanded={showLanguageDropdown}
                                        >
                                            <span className="selector-label d-none d-lg-inline">Language:</span>
                                            <span className="selector-value">{language}</span>
                                            <i className="fa fa-angle-down selector-arrow" />
                                        </button>
                                        {merchantSettings?.languages?.length > 1 && (
                                            <ul className={`selector-dropdown-menu ${showLanguageDropdown ? 'show' : ''}`}>
                                                {merchantSettings?.languages?.map((lang) => (
                                                    <li key={lang.id}>
                                                        <button
                                                            type="button"
                                                            className="selector-dropdown-item bg-transparent border-0 w-100 text-left"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleLanguageSelect(lang);
                                                                setShowLanguageDropdown(false);
                                                            }}
                                                        >
                                                            <span>{lang.language.name}</span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="header-action-pill d-flex align-items-center px-2 px-md-3 py-1 py-md-2 gap-3 gap-md-4">
                                {/* <button className="action-btn" onClick={() => dispatch(openModal("searchModal"))} title="Search" aria-label="Search">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                 </button> */}
                                <button className="action-btn d-none d-sm-flex" onClick={onLoginClick} title="Profile" aria-label="User Profile">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                </button>
                                <Link to="/cart" className="action-btn position-relative" title="Cart" aria-label="Shopping Cart">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
                                    {cartsCount > 0 && (
                                        <span className="cart-badge">{cartsCount}</span>
                                    )}
                                </Link>
                            </div>

                            <Link to="/productlist" className="btn-fine btn-primary-fine px-3 px-md-4 py-2 fs-xs">
                                <span className="d-none d-sm-inline">Order Now</span>
                                <span className="d-inline d-sm-none">Order</span>
                            </Link>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}>
                <div className="mobile-menu-drawer shadow-lg" onClick={(e) => e.stopPropagation()}>
                    <div className="drawer-header d-flex align-items-center justify-content-between p-4 border-bottom">
                        <img src={merchantSettings?.image || "/assets/img/logo.png"} alt="Logo" style={{ height: '28px' }} />
                        <button className="bg-transparent border-0 p-0 text-dark" onClick={toggleMobileMenu} aria-label="Close mobile navigation menu">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"></path></svg>
                        </button>
                    </div>
                    <div className="drawer-body p-4">
                        <div className="d-flex flex-column gap-4">
                            <Link to="/" className="nav-link fs-5 py-2 border-bottom d-flex justify-content-between align-items-center" onClick={toggleMobileMenu}>
                                Home <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </Link>
                            <Link to="/productlist" className="nav-link fs-5 py-2 border-bottom d-flex justify-content-between align-items-center" onClick={toggleMobileMenu}>
                                Menu <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </Link>
                            <Link to="/store" className="nav-link fs-5 py-2 border-bottom d-flex justify-content-between align-items-center" onClick={toggleMobileMenu}>
                                Locations <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </Link>

                            {merchantSettings?.config?.show_home_order === 1 && (
                                <Link to="/store?home_outlet=true" className="nav-link fs-5 py-2 border-bottom d-flex justify-content-between align-items-center" onClick={toggleMobileMenu}>
                                    Home Order <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                </Link>
                            )}
                            {merchantSettings?.config?.show_event_booking === 1 && (
                                <Link to="/book-event" className="nav-link fs-5 py-2 border-bottom d-flex justify-content-between align-items-center" onClick={toggleMobileMenu}>
                                    Book an Event <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                </Link>
                            )}

                            {/* Mobile Currency & Language */}
                            {(currency || language) && (
                                <div className="mobile-selectors pt-4 mt-2 d-flex flex-column gap-4">
                                    {currency && (
                                        <div className="mobile-selector-row border-bottom pb-4 mb-2">
                                            <span className="text-muted small fw-semibold">Currency</span>
                                            <div className="d-flex flex-wrap gap-3 mt-3">
                                                {merchantSettings?.currencies?.map((curr) => (
                                                    <button
                                                        key={curr.id}
                                                        className={`mobile-selector-chip ${currency === curr.currency.code ? 'active' : ''}`}
                                                        onClick={() => { handleCurrencySelect(curr); toggleMobileMenu(); }}
                                                    >
                                                        {curr.currency.code} ({curr.currency.symbol})
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {language && (
                                        <div className="mobile-selector-row py-2">
                                            <span className="text-muted small fw-semibold">Language</span>
                                            <div className="d-flex flex-wrap gap-3 mt-3">
                                                {merchantSettings?.languages?.map((lang) => (
                                                    <button
                                                        key={lang.id}
                                                        className={`mobile-selector-chip ${language === lang.language.name ? 'active' : ''}`}
                                                        onClick={() => { handleLanguageSelect(lang); toggleMobileMenu(); }}
                                                    >
                                                        {lang.language.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="mt-auto pt-5">
                            <button className="btn-fine btn-primary-fine w-100 py-3 rounded-3" onClick={() => { navigate('/productlist'); toggleMobileMenu(); }}>
                                Order Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx="true">{`
                .nav-link { text-decoration: none; transition: var(--transition); font-weight: 600; color: var(--text-main); }
                .nav-link:hover { color: var(--primary) !important; }
                
                .header-action-pill {
                    background: rgba(255, 255, 255, 0.5);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    border-radius: 99px;
                    transition: var(--transition);
                    padding: 0.6rem 1.5rem !important;
                    gap: 1.5rem !important;
                }
                
                .action-btn { background: transparent; border: none; padding: 0; color: var(--text-main); transition: var(--transition); display: flex; align-items: center; justify-content: center; }
                .action-btn:hover { color: var(--primary); transform: scale(1.1); }
                
                .cart-badge { position: absolute; top: -6px; right: -12px; background: var(--primary); color: white; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 10px; border: 2px solid white; min-width: 18px; text-align: center; }

                .logo-shrink { height: 28px !important; }
                .brand-logo { transition: transform 0.3s ease; }

                /* Currency / Language Selector */
                .currency-selector,
                .language-selector-wrapper {
                    position: relative;
                }

                .selector-trigger {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    text-decoration: none;
                    color: var(--text-main);
                    font-size: 12px;
                    font-weight: 600;
                    padding: 5px 10px;
                    border-radius: 20px;
                    background: rgba(0,0,0,0.04);
                    border: 1px solid rgba(0,0,0,0.08);
                    transition: var(--transition);
                    white-space: nowrap;
                }
                .selector-trigger:hover {
                    background: rgba(0,0,0,0.08);
                    color: var(--primary);
                    text-decoration: none;
                }
                .selector-label {
                    color: var(--text-muted);
                    font-size: 11px;
                    font-weight: 500;
                }
                .selector-value {
                    font-weight: 700;
                    font-size: 12px;
                }
                .selector-arrow {
                    font-size: 10px;
                    color: var(--text-muted);
                    margin-left: 2px;
                    transition: transform 0.2s;
                }

                .selector-dropdown-menu {
                    display: none;
                    position: absolute;
                    top: calc(100% + 6px);
                    right: 0;
                    background: #fff;
                    border: 1px solid rgba(0,0,0,0.08);
                    border-radius: 12px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
                    min-width: 160px;
                    list-style: none;
                    padding: 6px;
                    margin: 0;
                    z-index: 9999;
                    animation: fadeInDown 0.15s ease;
                }
                .selector-dropdown-menu.show {
                    display: block;
                }
                @keyframes fadeInDown {
                    from { opacity: 0; transform: translateY(-6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .selector-dropdown-item {
                    display: block;
                    padding: 8px 12px;
                    font-size: 13px;
                    font-weight: 500;
                    color: var(--text-main);
                    text-decoration: none;
                    border-radius: 8px;
                    transition: background 0.15s;
                    white-space: nowrap;
                }
                .selector-dropdown-item:hover {
                    background: rgba(0,0,0,0.04);
                    color: var(--primary);
                    text-decoration: none;
                }

                /* Mobile selector chips */
                .mobile-selector-chip {
                    border: 1px solid rgba(0,0,0,0.12);
                    border-radius: 20px;
                    padding: 4px 12px;
                    font-size: 12px;
                    font-weight: 600;
                    background: transparent;
                    color: var(--text-main);
                    cursor: pointer;
                    transition: var(--transition);
                }
                .mobile-selector-chip.active,
                .mobile-selector-chip:hover {
                    background: var(--primary);
                    color: white;
                    border-color: var(--primary);
                }

                /* Mobile Menu Styles */
                .mobile-menu-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100vh;
                    background: rgba(0,0,0,0.4);
                    backdrop-filter: blur(4px);
                    z-index: 9999;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.4s ease;
                }
                
                .mobile-menu-overlay.active {
                    opacity: 1;
                    visibility: visible;
                }
                
                .mobile-menu-drawer {
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 85%;
                    max-width: 320px;
                    height: 100vh;
                    background: #fff;
                    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    flex-direction: column;
                    overflow-y: auto;
                }
                
                .mobile-menu-overlay.active .mobile-menu-drawer {
                    left: 0;
                }

                @media (max-width: 991px) {
                    .brand-logo { margin: 0 !important; }
                    .header-action-pill { 
                        gap: 1.2rem !important; 
                        padding: 0.5rem 1rem !important;
                        background: rgba(255, 255, 255, 0.8) !important;
                    }
                }

                @media (max-width: 576px) {
                    .header-action-pill { gap: 0.9rem !important; padding: 0.4rem 0.8rem !important; }
                    .btn-fine { padding: 0.5rem 1rem !important; font-size: 10px !important; }
                    .logo-shrink, .brand-logo img { height: 26px !important; }
                }
            `}</style>
        </>
    );
};

export default Header;